import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, User, ArrowsClockwise, CheckCircle } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import DatePickerDMY from './DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '—';
  const p = String(d).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const REASONS = ['sale', 'donation', 'inheritance', 'other'];

const emptyForm = () => ({
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  ownership_percentage: 100,
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  status: 'active',
  reason: '',
  notes: '',
  // Phase 988 — Explicit per-owner opening balance. Defaults to €0.00
  // (NO automatic carry-forward from previous owner). Admins can set
  // this when a new owner agreed to take over part / all of the
  // previous owner's balance.
  opening_balance: 0,
  opening_balance_notes: '',
});

/**
 * Phase 282 — Owner History timeline for a single apartment.
 * Renders inside the Apartment Details "Owner History" tab.
 *
 * Props:
 *  - apartmentId (required)
 *  - apartmentNumber (optional, for dialog header)
 *  - onChanged (optional callback fired after create/update/delete so the
 *    parent can refresh apartment data — owner_name is auto-stamped server-side).
 */
export default function OwnerHistoryTab({ apartmentId, apartmentNumber = '', onChanged }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // owner_history_id or null (create)
  const [form, setForm] = useState(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRows = async () => {
    if (!apartmentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/apartments/${apartmentId}/owner-history`);
      setRows(res.data || []);
    } catch (err) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [apartmentId]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.owner_history_id);
    setForm({
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      phone: row.phone || '',
      email: row.email || '',
      ownership_percentage: row.ownership_percentage ?? 100,
      start_date: row.start_date || '',
      end_date: row.end_date || '',
      status: row.status || 'active',
      reason: row.reason || '',
      notes: row.notes || '',
      opening_balance: row.opening_balance ?? 0,
      opening_balance_notes: row.opening_balance_notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim() && !form.last_name?.trim()) {
      toast.error(t('firstName') + ' / ' + t('lastName'));
      return;
    }
    const payload = {
      ...form,
      ownership_percentage: parseFloat(form.ownership_percentage) || 0,
      opening_balance: parseFloat(form.opening_balance) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API}/apartment-owner-history/${editing}`, payload);
      } else {
        await axios.post(`${API}/apartment-owner-history`, { apartment_id: apartmentId, ...payload });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      await fetchRows();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      if (row.status === 'active') {
        await axios.put(`${API}/apartment-owner-history/${row.owner_history_id}`, {
          status: 'previous',
          end_date: new Date().toISOString().split('T')[0],
        });
      } else {
        await axios.put(`${API}/apartment-owner-history/${row.owner_history_id}`, { status: 'active' });
      }
      await fetchRows();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/apartment-owner-history/${deleteTarget.owner_history_id}`);
      toast.success(t('savedSuccess'));
      setDeleteTarget(null);
      await fetchRows();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('cannotDeleteOwnerLinked'));
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="apt-owner-history-tab">
      {/* Header */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <User size={16} weight="fill" className="text-zinc-600" />
            <div>
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('ownerHistoryTitle')}
              </h3>
              <p className="text-[11px] text-zinc-500">{t('ownerHistoryDesc')}</p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-zinc-950 rounded-none text-xs" data-testid="add-owner-history-btn">
              <Plus size={14} className="mr-1" />{t('addOwnerHistory')}
            </Button>
          )}
        </div>

        {/* Timeline list */}
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-400" data-testid="no-owner-history">
            {t('noOwnerHistory')}
          </div>
        ) : (
          <ol className="divide-y divide-zinc-100" data-testid="owner-history-list">
            {rows.map((row) => {
              const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—';
              const isActive = row.status === 'active';
              return (
                <li key={row.owner_history_id} className="px-5 py-4 flex items-start gap-4" data-testid={`owner-history-row-${row.owner_history_id}`}>
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center pt-1">
                    <span className={`block w-2.5 h-2.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-900" data-testid={`owner-name-${row.owner_history_id}`}>{fullName}</span>
                      <span className={`text-[10px] px-2 py-0.5 ${isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-zinc-100 text-zinc-600 border border-zinc-200'}`} data-testid={`owner-status-${row.owner_history_id}`}>
                        {isActive ? t('statusActive') : t('statusPrevious')}
                      </span>
                      {row.ownership_percentage != null && (
                        <span className="text-[10px] text-zinc-500 font-mono">{row.ownership_percentage}%</span>
                      )}
                      {row.reason && (
                        <span className="text-[10px] text-zinc-500 italic">
                          · {t('reason' + row.reason.charAt(0).toUpperCase() + row.reason.slice(1))}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      <span className="font-mono">{formatDateDMY(row.start_date)} → {formatDateDMY(row.end_date)}</span>
                      {row.phone && <span>{row.phone}</span>}
                      {row.email && <span>{row.email}</span>}
                    </div>
                    {row.notes && (
                      <p className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{row.notes}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleStatus(row)}
                        className="p-1.5 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
                        title={isActive ? t('markAsPrevious') : t('markAsActive')}
                        data-testid={`toggle-owner-status-${row.owner_history_id}`}
                      >
                        {isActive ? <ArrowsClockwise size={14} /> : <CheckCircle size={14} />}
                      </button>
                      <button
                        onClick={() => openEdit(row)}
                        className="p-1.5 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
                        title={t('edit')}
                        data-testid={`edit-owner-history-${row.owner_history_id}`}
                      >
                        <PencilSimple size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(row)}
                        className="p-1.5 hover:bg-red-50 text-zinc-400 hover:text-red-500"
                        title={t('delete')}
                        data-testid={`delete-owner-history-${row.owner_history_id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editing ? t('editOwnerHistory') : t('addOwnerHistory')}
              {apartmentNumber ? ` — #${apartmentNumber}` : ''}
            </DialogTitle>
            <DialogDescription className="text-[11px]">{t('autoCloseHint')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('firstName')}</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="owner-first-name"
                />
              </div>
              <div>
                <Label>{t('lastName')}</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="owner-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('phone')}</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="owner-phone"
                />
              </div>
              <div>
                <Label>{t('email')}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="owner-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t('ownershipPercentage')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.ownership_percentage}
                  onChange={(e) => setForm({ ...form, ownership_percentage: e.target.value })}
                  className="rounded-none mt-1 font-mono"
                  data-testid="owner-percentage"
                />
              </div>
              <div>
                <Label>{t('startDate')}</Label>
                <DatePickerDMY
                  value={form.start_date}
                  onChange={(v) => setForm({ ...form, start_date: v })}
                  className="mt-1 font-mono"
                  data-testid="owner-start-date"
                />
              </div>
              <div>
                <Label>{t('endDate')}</Label>
                <DatePickerDMY
                  value={form.end_date}
                  onChange={(v) => setForm({ ...form, end_date: v })}
                  className="mt-1 font-mono"
                  data-testid="owner-end-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('status')}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="owner-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('statusActive')}</SelectItem>
                    <SelectItem value="previous">{t('statusPrevious')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('reasonForChange')}</Label>
                <Select value={form.reason || '__none'} onValueChange={(v) => setForm({ ...form, reason: v === '__none' ? '' : v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="owner-reason-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {REASONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {t('reason' + r.charAt(0).toUpperCase() + r.slice(1))}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>
                  {t('openingBalanceForNewOwner') || 'Opening Balance for New Owner'}
                  {' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.opening_balance}
                  onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
                  placeholder="0.00"
                  className="rounded-none mt-1 font-mono"
                  data-testid="owner-opening-balance"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  {t('openingBalanceForNewOwnerHint') || 'Defaults to €0.00. Set only when the new owner explicitly agreed to take over part or all of the previous owner\'s balance. Positive = owed by owner; negative = credit in their favor.'}
                </p>
              </div>
              <div>
                <Label>
                  {t('openingBalanceNotes') || 'Opening Balance Notes'}
                  {' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span>
                </Label>
                <Input
                  value={form.opening_balance_notes}
                  onChange={(e) => setForm({ ...form, opening_balance_notes: e.target.value })}
                  placeholder={t('openingBalanceNotesPlaceholder') || 'e.g. Took over €200 from previous owner'}
                  className="rounded-none mt-1"
                  data-testid="owner-opening-balance-notes"
                />
              </div>
            </div>

            <div>
              <Label>{t('notes')}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-none mt-1 text-sm min-h-[70px]"
                data-testid="owner-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-owner-history-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteOwnerHistory')}</AlertDialogTitle>
            <AlertDialogDescription>{t('deleteOwnerHistoryConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="confirm-delete-owner-history">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
